import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import type { VehicleClass } from "../quotes/types";
import type { CostProfile, VehicleClassCatalog } from "./types";

interface ListBody {
  data?: CostProfile[];
  meta?: { totalItems: number; totalPages: number; currentPage: number };
}
interface Envelope<T> {
  data?: { data?: T };
}

export function useCostProfiles(options: { enabled?: boolean } = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.costProfiles.all(companyId),
    queryFn: ({ signal }) =>
      getJson<ListBody>("/costProfilesList", { page: 1, limit: 200 }, { signal }).then((b) => b.data ?? []),
    enabled: (options.enabled ?? true) && !!companyId,
    staleTime: 60_000,
  });
}

export function useCostProfile(id: string | null | undefined) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.costProfiles.detail(companyId, id ?? null),
    queryFn: ({ signal }) =>
      getJson<Envelope<CostProfile>>("/costProfiles", { id }, { signal }).then((b) => b.data?.data ?? null),
    enabled: !!id && !!companyId,
  });
}

/** Perfil efectivo para una clase (el de la empresa o el del sistema). */
export function useDefaultCostProfile(vehicleClass: VehicleClass | null | undefined) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.costProfiles.defaultFor(companyId, vehicleClass ?? null),
    queryFn: ({ signal }) =>
      getJson<Envelope<CostProfile & { source: "company" | "system_default" }>>(
        "/costProfiles/default",
        vehicleClass ? { vehicle_class: vehicleClass } : {},
        { signal },
      ).then((b) => b.data?.data ?? null),
    enabled: !!companyId,
    staleTime: 60_000,
  });
}

/** Catálogo estático de clases/modos (lo publica el API para no duplicarlo). */
export function useVehicleClassCatalog() {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.costProfiles.catalog(),
    queryFn: ({ signal }) =>
      getJson<Envelope<VehicleClassCatalog>>("/costProfiles/vehicleClasses", {}, { signal }).then(
        (b) => b.data?.data ?? null,
      ),
    enabled: !!companyId,
    staleTime: 24 * 60 * 60_000,
  });
}
